<template>
  <div class="content-details">
    <p-reading-progress />
    <div class="content-details__container">
      <div class="content-details__wrapper">
        <div class="content-details__content">
          <template v-if="!loading && content">
            <p-breadcrumb
              :items="[
                { label: $t('general.home'), to: '/' },
                ...(content?.categories || []).map((category, index) => {
                  let link = category.slug
                  if (0 < index && content?.categories?.[0]) {
                    link = `${content.categories[0].slug}/${category.slug}`
                  }
                  return {
                    label: category.title,
                    to: appLink(link),
                  }
                }),
                { label: content?.title || '', to: '' },
              ]"
            />

            <p-content-title
              :title="content.title"
              :date="content.published_at"
              :reading-time="content.read_duration_minutes"
            />
            <div class="content-details__tabs">
              <p-tab v-model="selectedTab">
                <p-tab-panel
                  v-if="content?.content?.web"
                  id="web"
                  :title="$t('general.deviceTabs.web')"
                >
                  <div class="content-details__body" v-html="content.content.web" />
                  <div class="content-details__author">
                    <p class="f-utility-14 t-fg-secondary">{{ $t('general.author') }}:</p>
                    <p class="f-utility-14-medium t-fg-primary">Paribu</p>
                  </div>
                </p-tab-panel>
                <p-tab-panel
                  v-if="content?.content?.mobile"
                  id="mobile"
                  :title="$t('general.deviceTabs.mobile')"
                >
                  <div class="content-details__body" v-html="content.content.mobile" />
                  <div class="content-details__author">
                    <p class="f-utility-14 t-fg-secondary">{{ $t('general.author') }}:</p>
                    <p class="f-utility-14-medium t-fg-primary">Paribu</p>
                  </div>
                </p-tab-panel>
              </p-tab>

              <p-content-sidebar
                v-if="!loading && content?.content && headings.length > 0"
                :content="content.content[selectedTab]"
                :title="content.title"
              />
            </div>

            <p-dynamic-form
              v-if="
                !formSubmitted &&
                  content?.form &&
                  content?.form?.fields &&
                  0 < content.form.fields.length
              "
              :schema="extendedFormSchema ? extendedFormSchema : {}"
              class="p-dynamic-form"
              @submit="handleDynamicFormSubmit"
            />
            <div v-if="formSubmitted" class="py-32 text-center">
              <p-icon icon="check" size="xxl" class="justify-content-center" />
              <h2 class="f-title-32-bold mt-32">{{ $t('form.submission.title') }}</h2>
              <p class="f-utility-16 mt-8 t-fg-secondary">
                {{ $t('form.submission.description') }}
              </p>
            </div>

            <div class="announcement-details__feedback">
              <p-divider />
              <p-help-feedback @on-feedback="handleFeedback" @on-submit="handleSubmit" />
              <p-divider />
            </div>
          </template>
        </div>

        <!-- Desktop Sidebar -->
      </div>
    </div>

    <!-- Mobile Float Button -->
    <p-content-float-button
      v-if="$device.isMobile && !loading && content?.content && headings.length > 0"
      @on-click="showMobileDrawer = true"
    />

    <!-- Mobile Drawer -->
    <p-drawer
      v-if="$device.isMobile && showMobileDrawer"
      :is-show-overlay="true"
      :steps="{ content: { title: 'İçerikler', url: 'content' } }"
      @on-close="showMobileDrawer = false"
    >
      <template #content>
        <div class="content-mobile-navigation">
          <nav class="content-mobile-navigation__nav">
            <ul class="content-mobile-navigation__list">
              <!-- Content Title -->
              <li v-if="content?.title" class="content-mobile-navigation__item">
                <span class="content-mobile-navigation__link f-utility-14 t-fg-accent">
                  {{ content.title }}
                </span>
              </li>

              <!-- Headings List -->
              <li
                v-for="(heading, index) in headings"
                :key="index"
                class="content-mobile-navigation__item"
              >
                <a
                  :href="`#${heading.id}`"
                  class="content-mobile-navigation__link f-utility-14 t-fg-secondary"
                  @click.prevent="handleMobileHeadingClick(heading.id)"
                >
                  {{ heading.text }}
                </a>
              </li>
            </ul>
          </nav>
        </div>
      </template>
    </p-drawer>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, nextTick } from 'vue'
import { useRoute } from 'vue-router'

import { useUserStore } from '@/store/user'
import { useAppLink } from '@/composables/useAppLink'
import PDynamicForm from '@/components/pages/Content/PDynamicForm/PDynamicForm.vue'
import PContentSidebar from '@/components/ui/PContentSidebar/PContentSidebar.vue'
import PContentFloatButton from '@/components/ui/PContentFloatButton/PContentFloatButton.vue'
import { useVoteById } from '@/api/useContent'
import { useFormSubmit } from '@/api/useForm'

interface Similar {
  id: string
  title: string
}

interface FormField {
  [key: string]: any
}

interface Form {
  name: string
  is_login_required: boolean
  fields: FormField[]
}

interface Category {
  id: string
  title: string
  slug: string
}

interface Content {
  id: string
  title: string
  published_at: string
  read_duration_minutes: number
  content: {
    mobile: string
    web: string
  }
  similar?: Similar[]
  form?: Form
  categories: Category[]
}

interface Heading {
  id: string
  text: string
}

const props = defineProps<{
  content: Content | null
  loading: boolean
}>()

const { appLink } = useAppLink()
const route = useRoute()
const { user } = useUserStore()
const { fetchVoteById } = useVoteById()
const { submitForm } = useFormSubmit()

const { $device } = useNuxtApp()
const selectedTab = ref('web')
const showMobileDrawer = ref(false)
const headings = ref<Heading[]>([])
const formSubmitted = ref(false)

const extendedFormSchema = computed(() => {
  const originalSchema = props.content?.form

  if (!originalSchema || !originalSchema.fields) return originalSchema

  if (!user.isAuthenticated) {
    const additionalFields = [
      {
        order: -2,
        name: 'name',
        required: true,
        notDisplay: false,
        maxLength: 0,
        minLength: 0,
        text: {
          subtitle: {
            tr: '',
            en: '',
          },
          title: {
            tr: 'Ad Soyad',
            en: 'Name Surname',
          },
          tooltip: {
            tr: '',
            en: '',
          },
          options: {
            tr: [],
            en: [],
          },
        },
        type: 'text',
        id: 'field_name',
      },
      {
        order: -1,
        name: 'email',
        required: true,
        notDisplay: false,
        maxLength: 0,
        minLength: 0,
        text: {
          subtitle: {
            tr: '',
            en: '',
          },
          title: {
            tr: 'E-Posta',
            en: 'Email',
          },
          tooltip: {
            tr: '',
            en: '',
          },
          options: {
            tr: [],
            en: [],
          },
        },
        type: 'email',
        id: 'field_email',
      },
    ]

    return {
      ...originalSchema,
      fields: [...additionalFields, ...originalSchema.fields],
    }
  }

  return originalSchema
})

const checkContentForMobile = () => {
  if (!props.content?.content) return

  if (props.content.content.mobile && ($device as any)?.isMobile) {
    selectedTab.value = 'mobile'
  }
}

const extractHeadings = () => {
  if (!props.content?.content) {
    headings.value = []
    return
  }

  const html = (props.content.content as any)[selectedTab.value] || ''
  if (!html || typeof html !== 'string') {
    headings.value = []
    return
  }

  const parser = new DOMParser()
  const doc = parser.parseFromString(html, 'text/html')
  const h2Elements = doc.querySelectorAll('h2')

  const cleaned: Heading[] = []
  Array.from(h2Elements).forEach((h2) => {
    const raw = h2.textContent || ''
    const text = raw.replace(/[\u00A0\u200B-\u200D\uFEFF]/g, '').trim()
    if (!text) return

    const id = h2.id || `heading-${cleaned.length}`
    cleaned.push({ id, text })
  })

  headings.value = cleaned
}

const handleFeedback = async (isHelpful: boolean) => {
  if (isHelpful && props.content?.id) {
    await fetchVoteById(props.content.id, selectedTab.value, isHelpful)
  }
}

const handleSubmit = async (data: any) => {
  if (props.content?.id) {
    await fetchVoteById(props.content.id, selectedTab.value, false, data.options, data.text)
  }
}

const handleDynamicFormSubmit = async (formData: Record<string, any>) => {
  if (props.content?.id) {
    const success = await submitForm(props.content.id, formData, selectedTab.value)

    if (success) {
      formSubmitted.value = true
    }
  }
}

// Ensure canonical URL with root category prefix
const ensureCanonicalUrl = () => {
  const detailSlug = route.params.detail?.toString() || ''
  const rootSlug = props.content?.categories?.[0]?.slug || ''
  if (!detailSlug || !rootSlug) return

  const currentParts = route.path.split('/').filter(Boolean)
  const last = currentParts[currentParts.length - 1]
  const prev = currentParts[currentParts.length - 2]
  if (last === detailSlug && prev !== rootSlug) {
    const target = appLink(`${rootSlug}/${detailSlug}`)
    // Avoid history pollution and loops
    navigateTo(target, { replace: true })
  }
}

const waitForElementById = async (
  id: string,
  maxAttempts = 20,
  intervalMs = 50,
): Promise<HTMLElement | null> => {
  return new Promise((resolve) => {
    let attemptsRemaining = maxAttempts
    const tryFind = () => {
      const el = document.getElementById(id)
      if (el) return resolve(el)
      if (attemptsRemaining <= 0) return resolve(null)
      attemptsRemaining -= 1
      setTimeout(tryFind, intervalMs)
    }
    tryFind()
  })
}

const handleMobileHeadingClick = async (headingId: string) => {
  const element = document.getElementById(headingId) || (await waitForElementById(headingId))
  if (!element) return

  const headerEl = document.querySelector('.header') as HTMLElement | null
  const readingBarEl = document.querySelector('.p-reading-progress') as HTMLElement | null
  const headerHeight = headerEl?.offsetHeight ?? 0
  const readingBarHeight = readingBarEl ? 4 : 0

  const targetY =
    window.scrollY + element.getBoundingClientRect().top - (headerHeight + readingBarHeight + 8)

  window.scrollTo({
    top: targetY,
    behavior: 'smooth',
  })

  showMobileDrawer.value = false
}

watch(selectedTab, async () => {
  await nextTick()
  extractHeadings()
})

watch(
  () => props.content,
  async () => {
    await nextTick()
    extractHeadings()
    ensureCanonicalUrl()
  },
)

onMounted(async () => {
  checkContentForMobile()
  await nextTick()
  extractHeadings()
  ensureCanonicalUrl()
})

definePageMeta({
  layout: 'default',
})
</script>
